import { SelectDescType } from 'components/SelectWithDescriptions/SelectWithDescriptions';
import { FormikHelpers, useFormik } from 'formik';
import useError from 'hooks/useError';
import useClientEducationMutation from 'hooks/webapi/client/useClientEducationMutation';
import useClientEmployerMutation, {
  ClientEmployerMutationOptions,
} from 'hooks/webapi/client/useClientEmployerMutation';
import { useClientEmployerQuery } from 'hooks/webapi/client/useClientEmployerQuery';
import useClientEmployerResponseMutation from 'hooks/webapi/client/useClientEmployerResponseMutation';
import { useClientEmploymentDetailsMutation } from 'hooks/webapi/client/useClientEmploymentDetailsMutation';
import { EmployerOption } from 'hooks/webapi/foxentry/useCompaniesByIdMutation';
import useClientBudget from 'hooks/webapi/useClientBudget';
import useClientEmploymentMutation from 'hooks/webapi/useClientEmploymentMutation';
import useClientHousehold, {
  ClientHousehold,
} from 'hooks/webapi/useClientHousehold';
import i18Next from 'i18next';
import {
  getEducationOptions,
  getIncomeSourceOptions,
  getOwnershipOptions,
} from 'pages/Registration/EducationEmployment/educationEmploymentOptions';
import validateFinanceForm, {
  BudgetForm,
} from 'pages/Registration/Finance/validateFinanceForm';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { BANK_ID } from 'Router/routes';
import {
  areEmployerStepsRequired,
  parseEmploymentDetails,
} from 'utils/employerClient';
import useClient, { EmploymentStatus } from 'hooks/webapi/useClient';

const SERVICE_ERROR = i18Next.t('error:validation.service_unavailable');

export const useFinance = () => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);

  // API Hooks
  const { clientBudgetInfo, postClientBudget, fetchClientBudgetInfo } =
    useClientBudget();
  const { updateHouseHold, clientHousehold, fetchClientHousehold } =
    useClientHousehold();
  const { data: clientEmployerData } = useClientEmployerQuery();
  const { mutateAsync: sendEmployerInfo } = useClientEmployerMutation();
  const { mutateAsync: sendCompleteEmployerJSONToCola } =
    useClientEmployerResponseMutation();
  const { mutateAsync: sendEducationInfo } = useClientEducationMutation();
  const { mutateAsync: updateEmployerType } = useClientEmploymentMutation();
  const { mutateAsync: fetchEmployerDetails } =
    useClientEmploymentDetailsMutation();
  const { client, fetchClient } = useClient();

  // Internal hooks
  const { showError } = useError();

  // States for profession field (special one because)
  const [professionOptions, setProfessionOptions] =
    useState<SelectDescType[]>();
  const [professionSelected, setProfessionSelected] =
    useState<SelectDescType>();

  const submitSuccessRef = useRef<boolean>(false);

  // Loading States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInitialData, setLoadingInitialData] = useState(false);

  // Submit function
  const onSubmitForm = useCallback(
    async (
      {
        monthlyIncome,
        monthlyExpenses,
        numberOfHouseholdMembers,
        numberOfEmployedHouseholdMembers,
        housingType,
        educationType,
        incomeSourceType,
        employer,
      }: BudgetForm,
      actions: FormikHelpers<BudgetForm>,
    ) => {
      try {
        setIsSubmitting(true);
        submitSuccessRef.current = true;

        // Prepare all promises upfront

        const budgetNoChanges =
          monthlyIncome === clientBudgetInfo?.monthlyIncome &&
          monthlyExpenses === clientBudgetInfo?.monthlyExpenses &&
          numberOfHouseholdMembers ===
            clientBudgetInfo?.numberOfHouseholdMembers &&
          numberOfEmployedHouseholdMembers ===
            clientBudgetInfo?.numberOfEmployedHouseholdMembers;

        // 1. Budget
        if (!budgetNoChanges) {
          await postClientBudget({
            body: {
              monthlyIncome: Number(monthlyIncome),
              monthlyExpenses: Number(monthlyExpenses),
              numberOfHouseholdMembers: Number(numberOfHouseholdMembers),
              numberOfEmployedHouseholdMembers: Number(
                numberOfEmployedHouseholdMembers,
              ),
            },
          })
            .then(async () => {
              fetchClientBudgetInfo();
            })
            .catch(() => {
              submitSuccessRef.current = false;
              actions.setFieldError('monthlyIncome', SERVICE_ERROR);
              actions.setFieldError('monthlyExpenses', SERVICE_ERROR);
              actions.setFieldError('numberOfHouseholdMembers', SERVICE_ERROR);
              actions.setFieldError(
                'numberOfEmployedHouseholdMembers',
                SERVICE_ERROR,
              );
            });
        }

        const householdNoChanges =
          numberOfHouseholdMembers ===
            clientBudgetInfo?.numberOfHouseholdMembers &&
          numberOfEmployedHouseholdMembers ===
            clientBudgetInfo?.numberOfEmployedHouseholdMembers &&
          housingType === clientHousehold?.ownership;

        // 2. Household update
        if (!householdNoChanges) {
          await updateHouseHold({
            body: {
              numberOfInhabitants: Number(numberOfHouseholdMembers),
              numberOfAdults: Number(numberOfEmployedHouseholdMembers),
              ownership: housingType as ClientHousehold['ownership'],
            },
          })
            .then(() => {
              fetchClientHousehold();
            })
            .catch(() => {
              submitSuccessRef.current = false;
              actions.setFieldError('numberOfHouseholdMembers', SERVICE_ERROR);
              actions.setFieldError(
                'numberOfEmployedHouseholdMembers',
                SERVICE_ERROR,
              );
              actions.setFieldError('housingType', SERVICE_ERROR);
            });
        }

        // 3. Education
        const educationNoChanges = educationType === client?.education;
        if (!educationNoChanges) {
          await sendEducationInfo({
            level: String(educationType),
          }).catch(() => {
            submitSuccessRef.current = false;
            actions.setFieldError('educationType', SERVICE_ERROR);
          });
        }

        // 4. Income source type
        const professionNoChanged =
          clientEmployerData?.employmentDetail === professionSelected?.value;
        const incomeNoChanges =
          client?.employer?.employmentStatus === incomeSourceType &&
          professionNoChanged;

        if (!incomeNoChanges) {
          const newEmploymentStatus = incomeSourceType as EmploymentStatus;
          await updateEmployerType({
            employmentStatus: newEmploymentStatus,
            employmentDetail: parseEmploymentDetails(
              newEmploymentStatus,
              professionSelected,
            ),
          }).catch(() => {
            submitSuccessRef.current = false;
            actions.setFieldError('incomeSourceType', SERVICE_ERROR);
          });
        }

        // 5. Employer + profession (conditional)
        const employerChanged =
          (typeof employer === 'string'
            ? employer
            : (employer as EmployerOption)?.data.name) !==
          client?.employer?.name;

        const isDefaultOriginalValue = educationType === client?.education;

        const areEmployersDetailsNeeded =
          areEmployerStepsRequired(incomeSourceType);

        if (areEmployersDetailsNeeded) {
          if (!isDefaultOriginalValue || employerChanged) {
            const sentRequest =
              typeof employer === 'string'
                ? sendManualEmployerData
                : sendFoundEmployerData;

            await sentRequest(employer).catch(() => {
              submitSuccessRef.current = false;
              actions.setFieldError('educationType', SERVICE_ERROR);
            });
          } else if (professionSelected?.value && !professionNoChanged) {
            const employmentStatus =
              clientEmployerData?.employmentStatus || 'EMPLOYEE';
            await sendEmployerInfo({
              employmentStatus,
              employmentDetail: parseEmploymentDetails(
                employmentStatus,
                professionSelected,
              ),
            }).catch(() => {
              submitSuccessRef.current = false;
              actions.setFieldError('educationType', SERVICE_ERROR); // uses same endpoint as educationType field
            });
          }
        }

        // Update client data
        await fetchClient();

        if (submitSuccessRef.current) navigate(BANK_ID);
      } catch {
        // Do nothing for now
      } finally {
        setIsSubmitting(false);
      }
    },
    [clientBudgetInfo, clientHousehold, client, professionSelected],
  );

  const formik = useFormik<BudgetForm>({
    initialValues: {},
    validate: validateFinanceForm,
    onSubmit: onSubmitForm, // submit on external function
  });

  // Initial state to load data
  useEffect(() => {
    (async () => {
      try {
        setLoadingInitialData(true);

        if (clientBudgetInfo) {
          await formik.setValues(
            (prevValues) => ({
              ...prevValues,
              monthlyIncome: clientBudgetInfo.monthlyIncome,
              monthlyExpenses: clientBudgetInfo.monthlyExpenses,
              numberOfHouseholdMembers:
                clientBudgetInfo.numberOfHouseholdMembers,
              numberOfEmployedHouseholdMembers:
                clientBudgetInfo.numberOfEmployedHouseholdMembers,
            }),
            true,
          );
        }
        if (client) {
          await formik.setValues(
            (prevValues) => ({
              ...prevValues,
              educationType: client.education,
              employer: client.employer?.name,
              incomeSourceType: client.employer?.employmentStatus,
            }),
            true,
          );
        }

        if (clientHousehold) {
          await formik.setFieldValue(
            'housingType',
            clientHousehold.ownership,
            true,
          );
        }

        const occupations = await fetchEmployerDetails();
        setProfessionOptions(
          occupations.map((occupation) => ({
            name: occupation.localizedName,
            desc: occupation.localizedDescription,
            value: occupation.name,
          })),
        );
      } catch (e) {
        if (e instanceof Error) {
          showError(e);
          return;
        }
        showError();
      } finally {
        setLoadingInitialData(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      !professionSelected &&
      clientEmployerData?.employmentDetail &&
      professionOptions
    ) {
      const nextProfession = professionOptions.find(
        (option) => option.value === clientEmployerData?.employmentDetail,
      );
      if (nextProfession) setProfessionSelected(nextProfession);
    }
  }, [clientEmployerData?.employmentDetail, professionOptions]);

  // - - - [START] Functions to send employer and profession field - - -
  const isWorkingForCompany = areEmployerStepsRequired(
    formik.values.incomeSourceType,
  );

  const sendManualEmployerData = async (
    educationValue: EmployerOption | string | undefined,
  ) => {
    if (!educationValue) return;

    const employmentStatus = clientEmployerData?.employmentStatus || 'EMPLOYEE';
    const employerInfoOptions: ClientEmployerMutationOptions = {
      name: educationValue as string,
      naceCodes: [],
      employmentStatus,
      employmentDetail: parseEmploymentDetails(
        employmentStatus,
        professionSelected,
      ),
    };
    await sendEmployerInfo(employerInfoOptions);
  };

  const sendFoundEmployerData = async (
    educationValue: EmployerOption | string | undefined,
  ) => {
    if (typeof educationValue === 'string') return;

    if (!educationValue?.data?.name) return;

    const industrySet = new Set(
      educationValue.data.nace.map((nace: { code: string }) =>
        nace.code.slice(0, 2),
      ),
    );

    const employmentStatus = clientEmployerData?.employmentStatus || 'EMPLOYEE';
    const employerInfoOptions = {
      name: educationValue.data.name,
      naceCodes: [...industrySet],
      address:
        educationValue.data.address &&
        `${educationValue.data.address?.data.streetWithNumber}, ${educationValue.data.address?.data.city}, ${educationValue.data.address?.data.zip}`,
      employmentStatus,
      employmentDetail: parseEmploymentDetails(
        employmentStatus,
        professionSelected,
      ),
    };

    await sendEmployerInfo(employerInfoOptions);
    await sendCompleteEmployerJSONToCola(educationValue.data);
  };
  // - - - [END] Functions to send employer and profession field - - -

  const handleSubmitBtnOrModal = () => {
    if (isWorkingForCompany && !showModal) {
      setShowModal(true);
    } else {
      formik.handleSubmit();
    }
  };

  // Skip SERVER ERROR
  const isFormikValidationsOK =
    formik.isValid ||
    Object.values(formik.errors).every((error) => error === SERVICE_ERROR);

  const isFormikValidationsOKWithoutEmployer =
    Object.keys(formik.errors).filter(
      (key) =>
        key !== 'employer' &&
        Boolean(formik.errors[key as keyof typeof formik.errors]),
    ).length === 0;

  const ownershipOptions = useMemo(getOwnershipOptions, []);
  const educationOptions = useMemo(getEducationOptions, []);
  const incomeOptions = useMemo(getIncomeSourceOptions, []);

  const showErrorField = (fieldName: keyof typeof formik.values) =>
    formik.errors[fieldName] && formik.touched[fieldName];

  const isOSVC = formik.values.incomeSourceType === 'SELF_EMPLOYED';

  return {
    formik,
    ownershipOptions,
    isFormikValidationsOK,
    isFormikValidationsOKWithoutEmployer,
    onSubmitForm,
    showErrorField,
    isSubmitting,
    isLoadingInitialData,
    isWorkingForCompany,
    professionOptions,
    professionSelected,
    setProfessionSelected,
    showModal,
    setShowModal,
    handleSubmitBtnOrModal,
    educationOptions,
    incomeOptions,
    isOSVC,
  };
};
